import { SpanStatusCode } from '@opentelemetry/api';
import { EmeraldError } from 'src/common/api/emerald-error';
import { withSpinner } from 'src/common/components/with-spinner/with-spinner';
import ElectronHelper from 'src/common/electron/ElectronHelper';
import { DeviceType } from 'src/common/enumerations/device-type';
import { EmeraldDialogTypes } from 'src/common/enumerations/emerald-dialog-types';
import { TouchPointNotFound } from 'src/common/helpers/error-code';
import { LOCAL_CULTURE } from 'src/common/helpers/localized-description-helper';
import XLRLogger from 'src/common/helpers/logger';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { IMessageModal } from 'src/common/interfaces/dtos/IMessageModal';
import { disablePayments, enablePayments } from 'src/devices/payment/pinpad-operations';
import i18n from 'src/i18n';
import { popupAsyncMessage } from 'src/messages/business-logic/popup-message';
import { getLogOffMenu } from 'src/pos-configuration/get-log-off-menu';
import { isEmeraldSellingService } from 'src/xlr-configuration/pos-service-type-utils';
import { DOMAIN_NAME } from '../../common/helpers/config';
import { LocalStorage } from '../../common/helpers/local-storage';
import { ITouchPointConfiguration } from '../../common/interfaces/dtos/ITouchPointConfiguration';
import { store } from '../../common/redux/configure-store';
import { PinPadActions } from '../../common/redux/reducers/pinpad-reducer';
import { TouchpointActions } from '../../common/redux/reducers/touchpoint-reducer';
import { getFullTouchPointConfiguration } from './service/get-touchpoint-config';
import { getTouchpointLocalizedResource } from './service/get-touchpoint-locale';
import { showTouchpointNotFoundModal } from './touchpoint-not-found-modal';

const DEFAULT_TOUCHPOINT_ID = '1';

export const updateTouchPointConfiguration = async (): Promise<
  ITouchPointConfiguration | undefined
> => {
  const touchPointId = await ElectronHelper.getTouchpointIdFromEnvironmentVariable();
  if (isEmeraldSellingService() && !LocalStorage.tillId() && !touchPointId) {
    throw new EmeraldError(TouchPointNotFound);
  }

  return await getConfiguration();
};

export const getConfiguration = async (): Promise<ITouchPointConfiguration | undefined> => {
  const prevTillId = LocalStorage.tillId() ?? DEFAULT_TOUCHPOINT_ID;
  const tillId = await getTillId();
  let touchPointConfiguration: ITouchPointConfiguration | undefined;
  const span = createSpan('touchpoint-get-configuration');

  try {
    touchPointConfiguration = await getFullTouchPointConfiguration(DOMAIN_NAME, tillId);
    const pinpadConfig = touchPointConfiguration.deviceList?.find(device => {
      const hasCCLType = device.Type === DeviceType.CCL;
      const hasTestStore = !!device.TestStoreNumber;
      return hasCCLType && hasTestStore;
    });
    const trainingStore =
      pinpadConfig?.TestStoreNumber || touchPointConfiguration.storeId?.toString();
    const storeId = touchPointConfiguration.storeId?.toString();
    const trainingMode = store.getState().posMode.trainingMode;
    if (trainingMode) {
      touchPointConfiguration.storeId = trainingStore;
    } else {
      touchPointConfiguration.storeId = storeId;
    }
    store.dispatch(PinPadActions.setConfiguration(pinpadConfig));

    if (touchPointConfiguration) {
      store.dispatch(TouchpointActions.setTouchpointConfig(touchPointConfiguration));
      LocalStorage.setStoreId(touchPointConfiguration.storeId ?? '');
      LocalStorage.setTillId(touchPointConfiguration.touchPointId ?? '');
      LocalStorage.setTouchpointGroupId(touchPointConfiguration.touchPointGroupId ?? '');
    }
    await getLogOffMenu();
    await getResources(touchPointConfiguration.touchPointApplicationId || '');
  } catch (err) {
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: 'Touchpoint configuration request error',
    });
    if (err.code === 'TouchPointNotFound') {
      XLRLogger.error({
        message: 'Touchpoint not found.',
        source: 'touchpoint-configuration.ts',
        data: err,
      });

      if (prevTillId === tillId || prevTillId === '') {
        await showTouchpointNotFoundModal(true);
        LocalStorage.clearLocalStorage();
      } else {
        LocalStorage.setTillId(prevTillId);
        await showTouchpointNotFoundModal();
      }

      endSpan(span);
      return updateTouchPointConfiguration();
    } else {
      const modal: IMessageModal = {
        acceptButtonLabel: 'Restart POS',
        hideCancelButton: true,
        onAccept: () => {
          window.location.reload();
        },
        type: EmeraldDialogTypes.Error,
        message: 'PosStartError',
        subMessage: err.code,
      };
      await popupAsyncMessage(modal);
      XLRLogger.error({
        message: err.message,
        source: 'touchpoint-configuration.ts',
        data: err,
      });
    }
  }

  endSpan(span);
  return touchPointConfiguration;
};

export const updatePinPadConfig = async () => {
  if (store.getState().pinPad.isPinpadConfigured) {
    await withSpinner(async () => {
      await disablePayments();
      await updateTouchPointConfiguration();
      await enablePayments();
    });
  }
};

const getResources = async (touchPointApplicationId: string): Promise<void> => {
  await getTouchpointLocalizedResource(touchPointApplicationId, LOCAL_CULTURE)
    .then(res => {
      if (res.resourceData) {
        const lng = res.culture?.substring(0, 2);
        i18n.addResourceBundle(lng, 'translation', res.resourceData, undefined, true);
      }
    })
    .catch(err => {
      XLRLogger.error(err);
    });
};

export const getTillId = async (): Promise<string> => {
  if (!isEmeraldSellingService()) {
    return LocalStorage.computerName() || DEFAULT_TOUCHPOINT_ID;
  }

  const envVarTouchpoint = await ElectronHelper.getTouchpointIdFromEnvironmentVariable();
  return envVarTouchpoint || LocalStorage.tillId() || DEFAULT_TOUCHPOINT_ID;
};
